import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { wsUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Users, Bot, Radio, Copy, Link2, Loader2, Swords } from "lucide-react";
import Game from "./Game";

const AUTO_BOT_AT = 20; // seconds
const BEST_OF = 3;      // Rockless is always Best-of-3

export default function Play() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRoom = params.get("room") || "";

  const [wsReady, setWsReady] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | queued | in_match
  const [elapsed, setElapsed] = useState(0);
  const [inviteCode, setInviteCode] = useState("");
  const [createdCode, setCreatedCode] = useState("");
  const [matchData, setMatchData] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);
  const [autoBotFired, setAutoBotFired] = useState(false);

  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const autoBotFiredRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem("rps_token");
    if (!token) {
      nav("/");
      return;
    }
    const ws = new WebSocket(`${wsUrl("/api/ws")}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => setWsReady(true);
    ws.onclose = () => setWsReady(false);
    ws.onerror = () => { /* silent */ };

    ws.onmessage = (evt) => {
      const msg = JSON.parse(evt.data);
      handleMessage(msg);
    };

    return () => {
      ws.close();
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (wsReady && initialRoom) {
      wsRef.current.send(JSON.stringify({ type: "join_room", code: initialRoom }));
      toast.info(`Joining room ${initialRoom}...`);
    }
  }, [wsReady, initialRoom]);

  const handleMessage = (msg) => {
    switch (msg.type) {
      case "connected":
        setOnlineCount(msg.online);
        break;
      case "queue_joined":
        setStatus("queued");
        setElapsed(0);
        setAutoBotFired(false);
        autoBotFiredRef.current = false;
        timerRef.current = setInterval(() => {
          setElapsed((e) => {
            const next = e + 1;
            if (next >= AUTO_BOT_AT && !autoBotFiredRef.current) {
              autoBotFiredRef.current = true;
              setAutoBotFired(true);
              toast.info("No humans found — sending in the bot!");
              wsRef.current?.send(JSON.stringify({ type: "play_bot", best_of: BEST_OF }));
            }
            return next;
          });
        }, 1000);
        break;
      case "queue_left":
        setStatus("idle");
        if (timerRef.current) clearInterval(timerRef.current);
        setElapsed(0);
        break;
      case "room_created":
        setCreatedCode(msg.code);
        toast.success(`Room ${msg.code} created — share the code!`);
        break;
      case "match_start":
        if (timerRef.current) clearInterval(timerRef.current);
        setElapsed(0);
        setStatus("in_match");
        setMatchData({
          match_id: msg.match_id,
          best_of: msg.best_of,
          opponent: msg.opponent,
          your_role: msg.your_role,
          private: !!msg.private,
        });
        break;
      case "match_end":
        // Refresh so the in-match HUD & victory screen show updated streaks & record glow instantly.
        refresh();
        break;
      case "error":
        toast.error(msg.message);
        break;
      default:
        break;
    }
  };

  const send = (data) => wsRef.current?.send(JSON.stringify(data));

  const joinQueue = () => send({ type: "queue_join", best_of: BEST_OF });
  const leaveQueue = () => send({ type: "queue_leave" });
  const playBot = () => send({ type: "play_bot", best_of: BEST_OF });
  const createRoom = () => send({ type: "create_room", best_of: BEST_OF });
  const joinRoom = () => {
    if (!inviteCode) return;
    send({ type: "join_room", code: inviteCode });
  };

  const shareLink = () => {
    const url = `${window.location.origin}/play?room=${createdCode}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied!");
  };

  const copyCode = () => {
    navigator.clipboard.writeText(createdCode);
    toast.success("Code copied!");
  };

  const handleGameEnd = () => {
    setStatus("idle");
    setMatchData(null);
    setCreatedCode("");
    refresh();
  };

  if (status === "in_match" && matchData) {
    return <Game key={matchData.match_id} ws={wsRef.current} matchData={matchData} onEnd={handleGameEnd} user={user} />;
  }

  const remaining = Math.max(0, AUTO_BOT_AT - elapsed);

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-slate-50">Enter the Arena</h1>
            <p className="text-slate-400 mt-1">Best of 3. First to two rounds. Ready your fist.</p>
          </div>
          <div className="chip chip-cyan" data-testid="online-count">
            <Radio className="w-3.5 h-3.5" /> {onlineCount} ONLINE
          </div>
        </div>

        {status === "idle" && (
          <>
            <div className="neo-flat p-6 sm:p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-xl text-slate-100">Match Format</div>
                  <div className="text-sm text-slate-400">
                    Best of 3 · First to 2 rounds · 15 s per move
                  </div>
                </div>
                <div className="chip chip-violet" data-testid="format-badge">BEST-OF-3</div>
              </div>

              <Button
                onClick={joinQueue}
                data-testid="matchmaking-play-button"
                disabled={!wsReady}
                className="w-full h-16 text-lg font-black tracking-wide border border-amber-400/60 bg-amber-400 hover:bg-amber-300 text-slate-950 transition"
              >
                <Swords className="w-5 h-5 mr-2" /> FIND MATCH
              </Button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button
                  onClick={playBot}
                  variant="outline"
                  data-testid="play-bot-button"
                  disabled={!wsReady}
                  className="h-12 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-cyan-200"
                >
                  <Bot className="w-4 h-4 mr-2" /> Play the Bot
                </Button>
                <Button
                  onClick={createRoom}
                  variant="outline"
                  data-testid="create-room-button"
                  disabled={!wsReady}
                  className="h-12 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800 hover:text-violet-200"
                >
                  <Link2 className="w-4 h-4 mr-2" /> Challenge a Friend
                </Button>
              </div>
            </div>

            {createdCode && (
              <div className="neo-flat glow-violet p-6 space-y-3" data-testid="created-room-card">
                <div className="font-display font-bold text-lg text-slate-100">Your friend room is live</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <code className="font-mono-stat text-2xl bg-violet-500/10 border border-violet-400/60 text-violet-200 px-4 py-2 rounded-lg" data-testid="room-code-display">
                    {createdCode}
                  </code>
                  <Button size="sm" onClick={copyCode} data-testid="copy-room-code-button" className="border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                    <Copy className="w-4 h-4 mr-1" /> Code
                  </Button>
                  <Button size="sm" onClick={shareLink} data-testid="copy-room-link-button" className="border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                    <Link2 className="w-4 h-4 mr-1" /> Link
                  </Button>
                </div>
                <p className="text-sm text-slate-400">Waiting for your friend to join…</p>
              </div>
            )}

            <div className="neo-flat p-6 space-y-3">
              <div className="font-display font-bold text-lg text-slate-100">Join a friend</div>
              <div className="flex gap-2">
                <Input
                  placeholder="RPS-1234"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  data-testid="join-room-code-input"
                  className="border-slate-700 bg-slate-900/70 text-slate-100"
                />
                <Button onClick={joinRoom} disabled={!inviteCode} data-testid="join-room-button" className="border border-cyan-400/60 bg-cyan-400 hover:bg-cyan-300 text-slate-950">
                  Join
                </Button>
              </div>
            </div>
          </>
        )}

        {status === "queued" && (
          <div className="neo-flat glow-cyan p-8 text-center space-y-6" data-testid="queue-screen">
            <div className="relative w-32 h-32 mx-auto">
              <div className="absolute inset-0 rounded-full bg-cyan-400/10 border border-cyan-400/60 flex items-center justify-center">
                <Users className="w-12 h-12 text-cyan-300" />
              </div>
              <div className="absolute inset-0 rounded-full bg-cyan-400/20 pulse-ring" />
            </div>
            <div>
              <div className="font-display font-black text-2xl text-slate-50">Searching for a challenger…</div>
              <div className="text-slate-400 mt-1 font-mono-stat text-sm">
                {elapsed}s elapsed • {onlineCount} online
              </div>
              {!autoBotFired ? (
                <div className="text-slate-400 mt-1 font-mono-stat text-xs" data-testid="bot-countdown">
                  Auto-bot in <span className="text-amber-300">{remaining}s</span>
                </div>
              ) : (
                <div className="text-amber-300 mt-1 font-mono-stat text-xs" data-testid="bot-autofire-notice">
                  Deploying bot opponent…
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={playBot}
                data-testid="queue-bot-fallback-button"
                className="border border-violet-400/60 bg-violet-400 hover:bg-violet-300 text-slate-950"
              >
                <Bot className="w-4 h-4 mr-2" /> Fight the Bot Now
              </Button>
              <Button variant="outline" onClick={leaveQueue} data-testid="leave-queue-button" className="border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
